import { isValidPublicId, resolveWirePublicId } from '../lib/publicId'
import { apiRequest } from './client'
import type { CreationTask } from './creations'
import { normalizeCreationTask } from './creations'

export type EpisodeJobPageStatus =
  | 'pending'
  | 'running'
  | 'awaiting_selection'
  | 'done'
  | 'failed'
  | 'cancelled'

export type ComicEpisodeGenerateJob = {
  publicId: string
  projectPublicId: string
  episodeId: string
  status: string
  cancelled: boolean
  currentPageIndex: number
  pages: {
    pageId: string
    title: string
    status: EpisodeJobPageStatus
    creationTaskId: string | null
    errorMessage: string | null
  }[]
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

type ComicEpisodeGenerateJobWire = ComicEpisodeGenerateJob & {
  id?: string
  public_id?: string
  projectId?: string
  project_public_id?: string
}

export function normalizeComicEpisodeGenerateJob(job: ComicEpisodeGenerateJobWire): ComicEpisodeGenerateJob {
  return {
    ...job,
    publicId: resolveWirePublicId(job, 'publicId', 'public_id', 'id'),
    projectPublicId: resolveWirePublicId(job, 'projectPublicId', 'project_public_id', 'projectId'),
  }
}

export function isValidComicEpisodeGenerateJobPublicId(value: string | null | undefined): boolean {
  return isValidPublicId(value)
}

export type ComicPageRegenerateMode = 'edit_text' | 'edit_image'

export type ComicPageGenerateRequestBody = {
  superResolution?: boolean
  regenerateMode?: ComicPageRegenerateMode
  baseCandidateId?: string | null
  editPrompt?: string
  referencePageImage?: boolean
}

export function createComicEpisodeGenerateJob(
  projectId: string,
  episodeId: string,
  body: {
    pageIds: string[]
  },
) {
  return apiRequest<ComicEpisodeGenerateJobWire>(
    `/v1/comic-projects/${encodeURIComponent(projectId)}/episodes/${encodeURIComponent(episodeId)}/generate-jobs`,
    { method: 'POST', body: JSON.stringify(body) },
  ).then(normalizeComicEpisodeGenerateJob)
}

export function createComicPageGenerateTask(
  projectId: string,
  episodeId: string,
  pageId: string,
  body?: ComicPageGenerateRequestBody,
) {
  return apiRequest<CreationTask & { id?: string; public_id?: string }>(
    `/v1/comic-projects/${encodeURIComponent(projectId)}/episodes/${encodeURIComponent(episodeId)}/pages/${encodeURIComponent(pageId)}/generate`,
    { method: 'POST', body: JSON.stringify(body ?? {}) },
  ).then(normalizeCreationTask)
}

/** 当前用户进行中的话级生图任务（换设备/刷新后恢复灵动岛） */
export function listActiveComicEpisodeGenerateJobs() {
  return apiRequest<ComicEpisodeGenerateJobWire[]>(
    '/v1/comic-episode-generate-jobs/active',
    { method: 'GET' },
  ).then((items) =>
    items
      .map(normalizeComicEpisodeGenerateJob)
      .filter((job) => isValidComicEpisodeGenerateJobPublicId(job.publicId)),
  )
}

export function getComicEpisodeGenerateJob(projectId: string, jobId: string) {
  return apiRequest<ComicEpisodeGenerateJobWire>(
    `/v1/comic-projects/${encodeURIComponent(projectId)}/generate-jobs/${encodeURIComponent(jobId)}`,
    { method: 'GET' },
  ).then(normalizeComicEpisodeGenerateJob)
}

export function cancelComicEpisodeGenerateJob(projectId: string, jobId: string) {
  return apiRequest<ComicEpisodeGenerateJobWire>(
    `/v1/comic-projects/${encodeURIComponent(projectId)}/generate-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  ).then(normalizeComicEpisodeGenerateJob)
}

export function resumeComicEpisodeGenerateJob(projectId: string, jobId: string) {
  return apiRequest<ComicEpisodeGenerateJobWire>(
    `/v1/comic-projects/${encodeURIComponent(projectId)}/generate-jobs/${encodeURIComponent(jobId)}/resume`,
    { method: 'POST' },
  ).then(normalizeComicEpisodeGenerateJob)
}

const TERMINAL_JOB = new Set(['succeeded', 'failed', 'cancelled'])

export async function pollComicEpisodeGenerateJob(
  projectId: string,
  jobId: string,
  options?: { intervalMs?: number; onUpdate?: (job: ComicEpisodeGenerateJob) => void },
): Promise<ComicEpisodeGenerateJob> {
  const intervalMs = options?.intervalMs ?? 1500
  for (;;) {
    const job = await getComicEpisodeGenerateJob(projectId, jobId)
    options?.onUpdate?.(job)
    if (TERMINAL_JOB.has(job.status)) {
      return job
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
}
